/**
 * React Query hooks for building operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBuildings,
  getBuilding,
  createBuilding,
  updateBuilding,
  deleteBuilding,
} from '../api/buildings';
import { communityKeys } from './useCommunities';

/** Query key factory for buildings. */
export const buildingKeys = {
  all: (communityId) => ['buildings', communityId],
  detail: (communityId, buildingId) => ['buildings', communityId, buildingId],
};

/**
 * List all buildings for a community.
 * @param {string|null} communityId - UUID
 */
export function useBuildings(communityId) {
  return useQuery({
    queryKey: buildingKeys.all(communityId),
    queryFn: () => getBuildings(communityId),
    enabled: !!communityId,
  });
}

/**
 * Get a single building with assets and datasets.
 * @param {string|null} communityId - UUID
 * @param {string|null} buildingId - UUID
 */
export function useBuilding(communityId, buildingId) {
  return useQuery({
    queryKey: buildingKeys.detail(communityId, buildingId),
    queryFn: () => getBuilding(communityId, buildingId),
    enabled: !!communityId && !!buildingId,
  });
}

/**
 * Create a new building in a community.
 * @param {string} communityId - UUID
 */
export function useCreateBuilding(communityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createBuilding(communityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildingKeys.all(communityId) });
      queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) });
    },
  });
}

/**
 * Update a building.
 * @param {string} communityId - UUID
 */
export function useUpdateBuilding(communityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ buildingId, data }) => updateBuilding(communityId, buildingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildingKeys.all(communityId) });
      queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) });
    },
  });
}

/**
 * Delete a building.
 * @param {string} communityId - UUID
 */
export function useDeleteBuilding(communityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (buildingId) => deleteBuilding(communityId, buildingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildingKeys.all(communityId) });
      queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) });
    },
  });
}
